import { map, concatMap } from 'rxjs/operators';
import { SharedService } from './../../shared.service';
import { Injectable } from '@angular/core';
import { CanDeactivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { HasErrors } from './HasErrors';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CheckForErrorsMessageComponent } from './check-for-errors-message/check-for-errors-message.component';
import { Location } from '@angular/common';

/**
 * This guard shows a warning when the user tries to navigate away from a page that has forms and errors.
 * The warning has three options:
 *      save and continue - the user will save the data and continue. This is only available if there are no errors.
 *      continue without save - the user will not save and continue.
 *      cancel - the user will stay on the apge.
 *
 * To use this guard, do the following:
 * 1. Implement HasErrors on the component.
 * 2. Register the guard in the CanDeactivate array of your route.
 *
 * This guard calls the checkErrors function of your component. The result determines the behavior of this guard.
 * See the canDeactivate function of this guard for more details.
 */
@Injectable()
export class CheckForErrorsGuard implements CanDeactivate<HasErrors> {

    // Properties
    warningDialog: MatDialogRef<CheckForErrorsMessageComponent>;
    savingObservable: Observable<any>;

    // Class Functions
    constructor(
        private dialog: MatDialog,
        private sharedService: SharedService,
        private router: Router,
        private location: Location
    ) {
    }

    // Methods
    checkForDeactivation(currentComponent: any = null): Observable<boolean> | boolean {
        // POST: checks the router state tree for itself and, if it's there, performs canDeactivate
        if (currentComponent) {
            if (currentComponent.checkErrors != undefined) {
                return this.canDeactivate(currentComponent);
            } else {
                return true;
            }
        } else {
            if (this.sharedService.currentComponent && this.sharedService.currentComponent.checkErrors != undefined) {
                return this.canDeactivate(this.sharedService.currentComponent);
            } else {
                return true;
            }
        }
    }

    urlTreeWorkaround(currentRoute: ActivatedRouteSnapshot) {
        const currentUrlTree = this.router.createUrlTree([], currentRoute);
        const currentUrl = currentUrlTree.toString();
        this.location.go(currentUrl);
        return false;
    }

    canDeactivate(component: HasErrors, currentRoute?: ActivatedRouteSnapshot, currentState?: RouterStateSnapshot, nextState?: RouterStateSnapshot): Observable<boolean> | boolean {
        // PRE: the user tries to navigate away
        // POST: checks the errors.
        //      If 5, waits for save and continues
        //      If 4, shows the warning dialog. If the result is true, it will wait for the save before it continues.
        //      If 3, shows the warning, but doesn't perform route on the save (lets the component do it)
        //      If 2, continues without save.
        //      If 1, continues with save.
        //      If 0, shows warning.
        //      If -1, stops the route.
        let saved = null;
        switch (component.checkErrors(nextState)) {
            case 5:
                saved = component.save();
                if (saved instanceof Observable) {
                    return saved;
                } else {
                    return of(true);
                }
            case 4:
                const dialog = this.showWarningWithoutSave(component);
                return dialog.pipe(
                    concatMap(result => {
                        if (result == 1) {
                            saved = component.save();
                            if (saved instanceof Observable) {
                                return saved;
                            } else {
                                return of(true);
                            }
                        } else {
                            if (result == 0) {
                                if (component['continuedWithoutSave'] != null && typeof component['continuedWithoutSave'] == 'function') {
                                    component.continuedWithoutSave();
                                }
                                return of(true);
                            } else {
                                return of(this.urlTreeWorkaround(currentRoute));
                            }
                        }
                    })
                );
            case 3:
                return this.showWarningWithoutSaveRoute(component, currentRoute);
            case 2:
                if (component['continuedWithoutSave'] != null && typeof component['continuedWithoutSave'] == 'function') {
                    component.continuedWithoutSave();
                }
                return true;
            case 1:
                component.save();
                return true;
            case 0:
                return this.showWarning(component, currentRoute);
            case -1:
                return this.urlTreeWorkaround(currentRoute);
        }
    }

    showWarning(component: HasErrors, currentRoute?: ActivatedRouteSnapshot) {
        // POST: shows a warning dialog. If the user clicks OK, returns true. Else, returns false.
        this.warningDialog = this.dialog.open(CheckForErrorsMessageComponent, {
            data: {
                errors: component.errors
            },
            disableClose: true
        });
        this.warningDialog.componentInstance.dialogBox = this.warningDialog;
        return this.warningDialog.componentInstance.saveChoice.pipe(map(result => {
            switch (result) {
                case 1:
                    if (component.errors.length > 0) {
                        return this.urlTreeWorkaround(currentRoute);
                    } else {
                        component.save();
                        return true;
                    }
                case 0:
                    if (component['continuedWithoutSave'] != null && typeof component['continuedWithoutSave'] == 'function') {
                        component.continuedWithoutSave();
                    }
                    return true;
                case -1:
                    return this.urlTreeWorkaround(currentRoute);
            }
        }));
    }

    showWarningWithoutSave(component: HasErrors) {
        // POST: shows a warning dialog, but this one does not perform the save.
        //       As a consequence, it returns 1 for saved, 0 for continue without save, and -1 for no save
        this.warningDialog = this.dialog.open(CheckForErrorsMessageComponent, {
            data: {
                errors: component.errors
            },
            disableClose: true
        });
        this.warningDialog.componentInstance.dialogBox = this.warningDialog;
        return this.warningDialog.componentInstance.saveChoice.pipe(map(result => {
            switch (result) {
                case 1:
                    if (component.errors.length > 0) {
                        return -1;
                    } else {
                        return 1;
                    }
                case 0:
                    return 0;
                case -1:
                    return -1;
            }
        }));
    }

    showWarningWithoutSaveRoute(component: HasErrors, currentRoute?: ActivatedRouteSnapshot) {
        // POST: shows a warning dialog. If the user clicks OK, returns true. Else, returns false.
        this.warningDialog = this.dialog.open(CheckForErrorsMessageComponent, {
            data: {
                errors: component.errors
            },
            disableClose: true
        });
        this.warningDialog.componentInstance.dialogBox = this.warningDialog;
        return this.warningDialog.componentInstance.saveChoice.pipe(map(result => {
            switch (result) {
                case 1:
                    if (component.errors.length == 0) {
                        component.save();
                    }
                    return this.urlTreeWorkaround(currentRoute);
                case 0:
                    if (component['continuedWithoutSave'] != null && typeof component['continuedWithoutSave'] == 'function') {
                        component.continuedWithoutSave();
                    }
                    return true;
                case -1:
                    return this.urlTreeWorkaround(currentRoute);
            }
        }));
    }
}
